// src/pages/PaymentSuccess.tsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { C, FONT } from "../theme";

/**
 * Shown when BOG redirects the coach back after a SUCCESSFUL payment.
 *
 * IMPORTANT: With real BOG integration, the registrations are written
 * server-side by bogWebhook AFTER this page loads. There is nothing in
 * React state to display here. The coach receives a confirmation email
 * (sent by bogWebhook). This page simply reassures them and directs
 * them to check their history.
 *
 * Do NOT try to read registration data from Firestore here — the webhook
 * may still be processing when this page renders.
 */
export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(8);

  // Pull context written by Register.tsx just before the BOG redirect.
  // Read once on mount; clear immediately so a reload of /success after
  // navigating away doesn't show stale values.
  const [pendingOrderId, setPendingOrderId] = useState<string | null>(null);
  const [pendingCompetitionName, setPendingCompetitionName] = useState<string | null>(null);
  useEffect(() => {
    try {
      const orderId = sessionStorage.getItem("pendingOrderId");
      const compName = sessionStorage.getItem("pendingCompetitionName");
      setPendingOrderId(orderId);
      setPendingCompetitionName(compName);
      sessionStorage.removeItem("pendingOrderId");
      sessionStorage.removeItem("pendingCompetitionId");
      sessionStorage.removeItem("pendingCompetitionName");
    } catch {
      // sessionStorage unavailable — fall back to generic message.
    }
  }, []);

  // Auto-redirect to history after 8 seconds
  useEffect(() => {
    if (countdown <= 0) {
      navigate("/history", { replace: true });
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, navigate]);

  return (
    <div style={s.page}>
      <Navbar />
      <div style={s.centeredWrapper}>
        <div style={s.card}>
          {/* Icon */}
          <div style={s.iconCircle}>
            <span style={s.icon}>✓</span>
          </div>

          {/* Title */}
          <div style={s.title}>Payment Successful</div>
          <div style={s.titleKa}>გადახდა წარმატებულია</div>

          <div style={s.subtitle}>
            Your payment has been received by Bank of Georgia.
            <br />
            Your athletes are being registered now.
            <br />
            <br />
            თქვენი გადახდა მიღებულია საქართველოს ბანკის მიერ.
            <br />
            სპორტსმენების რეგისტრაცია მიმდინარეობს.
          </div>

          {/* Reference + competition — populated from sessionStorage written
              by Register.tsx just before the BOG redirect. */}
          {(pendingOrderId || pendingCompetitionName) && (
            <div style={s.refBox}>
              {pendingCompetitionName && (
                <div style={s.refRow}>
                  <span style={s.refLabel}>Competition / შეჯიბრება</span>
                  <span style={s.refValueComp}>{pendingCompetitionName}</span>
                </div>
              )}
              {pendingOrderId && (
                <div style={s.refRow}>
                  <span style={s.refLabel}>Order reference / საცნობარო კოდი</span>
                  <span style={s.refValueCode}>{pendingOrderId}</span>
                </div>
              )}
            </div>
          )}

          {/* Info box */}
          <div style={s.infoBox}>
            <div style={s.infoTitle}>📧 Check your email / შეამოწმეთ ელ. ფოსტა</div>
            <div style={s.infoText}>
              A confirmation email with your reference code and full list of
              registered athletes has been sent to your email address.
              <br /><br />
              დადასტურების წერილი საცნობარო კოდით და სრული სიით გამოგეგზავნათ ელ. ფოსტაზე.
            </div>
          </div>

          {/* Actions */}
          <div style={s.actions}>
            <button
              style={s.primaryBtn}
              onClick={() => navigate("/history")}
            >
              View My Registration History / ჩემი რეგისტრაციების ნახვა →
            </button>
            <button
              style={s.secondaryBtn}
              onClick={() => navigate("/dashboard")}
            >
              Back to Competitions / კონკურსების სია
            </button>
          </div>

          {/* Countdown */}
          <div style={s.countdown}>
            Redirecting to your history in {countdown} seconds...
            <br />
            {countdown} წამში გადაგამისამართებთ ისტორიაში...
          </div>
        </div>
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    backgroundColor: C.bg,
  },
  centeredWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "60px 16px",
  },
  card: {
    background: "var(--color-surface)",
    borderRadius: "16px",
    border: "1px solid var(--color-border)",
    boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
    padding: "48px 40px",
    maxWidth: "480px",
    width: "100%",
    textAlign: "center" as const,
  },
  iconCircle: {
    width: "64px",
    height: "64px",
    borderRadius: "50%",
    backgroundColor: "var(--color-success-soft)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 20px",
  },
  icon: {
    fontSize: "28px",
    fontWeight: 800,
    color: "var(--color-success)",
    lineHeight: 1,
  },
  title: {
    fontFamily: FONT?.display ?? "inherit",
    fontSize: "22px",
    fontWeight: 800,
    color: C.text,
    marginBottom: "4px",
    letterSpacing: "0.5px",
  },
  titleKa: {
    fontSize: "16px",
    fontWeight: 700,
    color: C.text2 ?? "#475569",
    marginBottom: "14px",
  },
  subtitle: {
    fontSize: "14px",
    color: C.text2 ?? "#64748b",
    lineHeight: 1.7,
    marginBottom: "24px",
  },
  refBox: {
    background: C.surface2,
    border: `1px solid ${C.border ?? "#e2e8f0"}`,
    borderRadius: "10px",
    padding: "14px 18px",
    marginBottom: "16px",
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
    textAlign: "left" as const,
  },
  refRow: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "2px",
  },
  refLabel: {
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "1px",
    color: C.text3 ?? "#94a3b8",
    textTransform: "uppercase" as const,
  },
  refValueComp: {
    fontSize: "14px",
    fontWeight: 700,
    color: C.text,
  },
  refValueCode: {
    fontFamily: "monospace",
    fontSize: "14px",
    fontWeight: 700,
    color: C.red ?? "#da291c",
    letterSpacing: "1px",
    wordBreak: "break-all" as const,
  },
  infoBox: {
    background: C.successDim,
    border: `1px solid ${C.successBorder}`,
    borderRadius: "10px",
    padding: "16px 20px",
    textAlign: "left" as const,
    marginBottom: "28px",
  },
  infoTitle: {
    fontSize: "13px",
    fontWeight: 700,
    color: C.success,
    marginBottom: "8px",
  },
  infoText: {
    fontSize: "13px",
    color: C.success,
    lineHeight: 1.7,
  },
  actions: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "10px",
    marginBottom: "16px",
  },
  primaryBtn: {
    backgroundColor: C.red ?? "#da291c",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "13px 24px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
    letterSpacing: "0.3px",
  },
  secondaryBtn: {
    backgroundColor: "transparent",
    color: C.text,
    border: `1px solid ${C.border2 ?? "#cbd5e1"}`,
    borderRadius: "8px",
    padding: "13px 24px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
  },
  countdown: {
    fontSize: "12px",
    color: C.text3 ?? "#94a3b8",
    lineHeight: 1.6,
  },
};
